import React, { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import {
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import "../../styles/assetregister.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import AssetModal from "../assetmodal";
import DeleteConfirmationModal from "../deleteconfirmationmodal";
import { API_BASE_URL } from "../../Config/config";

const AssetRegister = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [products, setProducts] = useState([]);
  const [searchId, setSearchId] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [newProduct, setNewProduct] = useState({
    iridis_id: "",
    asset_name: "",
    serial_number: "",
    asset_location: "",
    assignedTo: "",
    asset_type: "non-movable",
    id: 0,
    productId: "",
  });

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Calculate pagination
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredProducts.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchId]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const getVisiblePages = () => {
    const delta = isMobile ? 1 : 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else {
      if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }
    }

    return rangeWithDots;
  };

  // Post data
  const postProductsToBackend = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/assets/create`,
        newProduct
      );
      //console.log("Data posted successfully:", response.data);
    } catch (error) {
      //console.error("Error posting data to the middleware:", error);
      throw error;
    }
  };

  const closeModal = () => {
    if (isLoading) return;
    setIsModalOpen(false);
    setIsEditing(false);
    setSelectedProduct(null);
    setNewProduct({
      iridis_id: "",
      asset_name: "",
      serial_number: "",
      asset_location: "",
      assignedTo: "",
      asset_type: "fixed",
      id: 0,
      productId: "",
    });
    setErrorMessage("");
  };

  const handleAddProduct = () => {
    setIsModalOpen(true);
    setSelectedProduct(null);
    setIsEditing(false);
    setNewProduct({
      iridis_id: "",
      asset_name: "",
      serial_number: "",
      asset_location: "",
      assignedTo: "",
      asset_type: "fixed",
      id: 0,
      productId: "",
    });
    setErrorMessage("");
  };

  const handleSubmitAddProduct = async () => {
    // Validate required fields
    if (
      !newProduct.iridis_id.trim() ||
      !newProduct.asset_name.trim() ||
      !newProduct.serial_number.trim() ||
      !newProduct.asset_location.trim()
    ) {
      setErrorMessage("All fields are required.");
      return;
    }

    // Set loading state
    setIsLoading(true);
    setLoadingMessage(isEditing ? "Updating asset..." : "Adding asset...");

    try {
      if (isEditing) {
        await axios.put(
          `${API_BASE_URL}/assets/update/${selectedProduct.iridis_id}`,
          newProduct
        );
        toast.success("Asset updated successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        // Add new product
        await postProductsToBackend();
        toast.success("Asset added successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }

      // Reload assets
      await fetchAssets();
    } catch (error) {
      //console.error(error);
      toast.error(
        isEditing ? "Failed to update asset." : "Failed to add asset.",
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      setIsLoading(false);
      closeModal();
    }
  };

  const handleEditClick = (product) => {
    if (isLoading) return;
    setNewProduct(product);
    setIsModalOpen(true);
    setIsEditing(true);
    setSelectedProduct(product);
    setErrorMessage("");
  };

  // Function to open delete modal
  const handleDeleteClick = (product) => {
    if (isLoading || isDeleting) return;
    setProductToDelete(product);
    setIsDeleteModalOpen(true);
  };

  // Function to close delete modal
  const handleCloseDeleteModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setProductToDelete(null);
  };

  // Function to confirm delete
  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);

    try {
      await axios.delete(
        `${API_BASE_URL}/assets/delete/${productToDelete.iridis_id}`
      );

      toast.success("Asset deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Reload assets
      await fetchAssets();

      // Close the modal
      setIsDeleteModalOpen(false);
      setProductToDelete(null);
    } catch (error) {
      //console.error("Error deleting product:", error);
      // Show error message
      toast.error("Failed to delete asset. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  //  download the asset register as a CSV file
  const handleDownloadAll = () => {
    const csvRows = [];
    csvRows.push([
      "Iridis ID Number",
      "Asset Name",
      "Serial No",
      "Location",
      "Asset Type",
    ]);

    products.forEach((product) => {
      csvRows.push([
        product.iridis_id,
        product.asset_name,
        product.serial_number,
        product.asset_location,
        product.asset_type,
      ]);
    });

    const csvString = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", "asset_register.csv");
    a.click();

    window.URL.revokeObjectURL(url);
  };

  // fetch products
  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/assets`);
      //console.log("Fetched assets:", response.data);
      if (Array.isArray(response.data.assets)) {
        setProducts(response.data.assets);
        setFilteredProducts(response.data.assets);
      } else {
        //console.error("Fetched data is not an array:", response.data);
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (error) {
      //console.error("Error fetching assets:", error);
      toast.error("Failed to fetch assets. Please try again later.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  // Real-time search effect
  useEffect(() => {
    if (searchId.trim() === "") {
      setFilteredProducts(products);
    } else {
      const searchTerm = searchId.toLowerCase();
      const foundAssets = products.filter((product) => {
        return (
          product.iridis_id?.toLowerCase().includes(searchTerm) ||
          product.asset_name?.toLowerCase().includes(searchTerm) ||
          product.serial_number?.toLowerCase().includes(searchTerm) ||
          product.asset_location?.toLowerCase().includes(searchTerm) ||
          product.assignedTo?.toLowerCase().includes(searchTerm) ||
          product.asset_type?.toLowerCase().includes(searchTerm)
        );
      });
      setFilteredProducts(foundAssets);
    }
  }, [searchId, products]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchId(e.target.value);
  };

  // Mobile Card Component for Assets
  const AssetCard = ({ product }) => (
    <Card
      key={product.id}
      sx={{
        mb: 2,
        boxShadow: 2,
        "&:hover": {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "12px",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#007bff" }}
          >
            {product.asset_name}
          </Typography>
          <div>
            <IconButton
              size="small"
              onClick={() => handleEditClick(product)}
              disabled={isLoading || isDeleting}
              sx={{ mr: 0.5, color: "#007bff" }}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(product)}
              disabled={isLoading || isDeleting}
              sx={{ color: "#dc3545" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Iridis ID:</strong> {product.iridis_id}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Serial No:</strong> {product.serial_number}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Location:</strong> {product.asset_location}
        </Typography>
      </CardContent>
    </Card>
  );

  // Pagination Component
  const PaginationControls = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "20px",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "15px" : "0",
      }}
    >
      {/* Items per page selector */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Items per page</InputLabel>
          <Select
            value={itemsPerPage}
            label="Items per page"
            onChange={handleItemsPerPageChange}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Showing {Math.min(startIndex + 1, totalItems)} -{" "}
          {Math.min(endIndex, totalItems)} of {totalItems} items
        </Typography>
      </div>

      {/* Pagination buttons */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            size="small"
          >
            <FirstPageIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>

          {getVisiblePages().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === "number" && handlePageChange(page)}
              disabled={typeof page !== "number"}
              style={{
                padding: "8px 12px",
                border: "1px solid #ddd",
                backgroundColor:
                  page === currentPage ? "#007bff" : "transparent",
                color: page === currentPage ? "white" : "#333",
                cursor: typeof page === "number" ? "pointer" : "default",
                borderRadius: "4px",
                fontSize: "14px",
                minWidth: "40px",
              }}
            >
              {page}
            </button>
          ))}

          <IconButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            size="small"
          >
            <LastPageIcon />
          </IconButton>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="assetregister-container"
      style={{
        padding: isMobile ? "10px" : "20px",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <h1
        className="assetregister-title"
        style={{
          fontSize: isMobile ? "24px" : "30px",
          color: "#000000",
          marginBottom: isMobile ? "15px" : "20px",
          textAlign: isMobile ? "center" : "left",
        }}
      >
        Asset Register
      </h1>

      <ToastContainer />

      <div
        className="button-container"
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "10px" : "15px",
          marginBottom: "20px",
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        <input
          type="text"
          className="search-input"
          value={searchId}
          onChange={handleSearchChange}
          placeholder="Search for assets..."
          disabled={isLoading || isDeleting}
          style={{
            flex: isMobile ? "none" : "nones",
            padding: isMobile ? "12px" : "10px",
            fontSize: isMobile ? "16px" : "14px",
            minWidth: 0,
          }}
        />

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexDirection: isSmallMobile ? "column" : "row",
          }}
        >
          <button
            className="addproduct-button"
            onClick={handleAddProduct}
            disabled={isLoading || isDeleting}
            style={{
              padding: isMobile ? "12px 16px" : "10px 16px",
              fontSize: isMobile ? "14px" : "13px",
              whiteSpace: "nowrap",
            }}
          >
            Add Asset
          </button>

          <button
            className="download-button"
            onClick={handleDownloadAll}
            disabled={isLoading || isDeleting}
            style={{
              padding: isMobile ? "12px 16px" : "10px 16px",
              fontSize: isMobile ? "14px" : "13px",
              whiteSpace: "nowrap",
            }}
          >
            Download All
          </button>
        </div>
      </div>

      {/* Desktop Table View */}
      {!isMobile && (
        <div style={{ overflowX: "auto" }}>
          <table className="assetregister-table">
            <thead>
              <tr>
                <th>Iridis ID Number</th>
                <th>Asset Name</th>
                <th>Serial No</th>
                <th>Location</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    {searchId.trim() !== ""
                      ? "No assets found matching your search."
                      : "No assets available. Please add an asset."}
                  </td>
                </tr>
              ) : (
                currentItems.map((product) => (
                  <tr key={product.id}>
                    <td>{product.iridis_id}</td>
                    <td>{product.asset_name}</td>
                    <td>{product.serial_number}</td>
                    <td>{product.asset_location}</td>
                    <td>
                      <button
                        className="edit-button"
                        onClick={() => handleEditClick(product)}
                        disabled={isLoading || isDeleting}
                      >
                        <EditOutlined />
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteClick(product)}
                        disabled={isLoading || isDeleting}
                      >
                        <DeleteIcon />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div style={{ marginTop: "10px" }}>
          {currentItems.length === 0 ? (
            <Card sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {searchId.trim() !== ""
                  ? "No assets found matching your search."
                  : "No assets available. Please add an asset."}
              </Typography>
            </Card>
          ) : (
            currentItems.map((product) => (
              <AssetCard key={product.id} product={product} />
            ))
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {filteredProducts.length > 0 && <PaginationControls />}

      <AssetModal
        isOpen={isModalOpen}
        isEditing={isEditing}
        newProduct={newProduct}
        setNewProduct={setNewProduct}
        errorMessage={errorMessage}
        isLoading={isLoading}
        loadingMessage={loadingMessage}
        onClose={closeModal}
        onSubmit={handleSubmitAddProduct}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Asset"
        message="Are you sure you want to delete this asset?"
        itemName={productToDelete?.asset_name}
        confirmText="Delete Asset"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AssetRegister;
